import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Bot, Plus, Pencil, Play, Pause, Trash2, AlertTriangle, Inbox, RefreshCw } from "lucide-react";
import { Toast, ConfirmDialog, FieldLabel, StatusBadge, RowSkeleton, EmptyState, ErrorState, ActionMenu, Modal, SearchBox } from "./Sharedui";
import "./custom-theme.css";
import * as botApi from "../api/services/botService";
import { getAiModels } from "../api/services/aiService";

/* ============================================================
   API SERVICE LAYER
   Every call goes through the shared apiClient (src/api), which talks
   to the mock backend today and the Python backend when
   REACT_APP_USE_MOCK=false. Endpoint mapping:

     botService.list()          -> GET    /api/bots
     botService.create()        -> POST   /api/bots
     botService.update()        -> PATCH  /api/bots/{id}
     botService.setStatus()     -> PATCH  /api/bots/{id}  { status }
     botService.remove()        -> DELETE /api/bots/{id}
   ============================================================ */

const botService = {
  list: () => botApi.listBotsWithMeta(),
  create: (payload) => botApi.createBot(payload),
  update: (id, payload) => botApi.updateBot(id, payload),
  setStatus: (id, status) => botApi.setBotStatus(id, status),
  remove: (id) => botApi.deleteBot(id),
};


/* ============================================================
   ROLE-BASED ACCESS (Starter Pack §5 Role & Permission Matrix)
   ============================================================ */

const ROLES_THAT_CAN_MANAGE_BOTS = ["platform_admin", "business_admin"];
const BOT_VIEW_ROLES = [
  "platform_admin",
  "business_admin",
  "support_agent",
  "analyst_manager",
  "developer_integrator",
];

const ROLE_LABELS = {
  platform_admin: "Platform Admin",
  business_admin: "Business Admin",
  support_agent: "Support Agent",
  analyst_manager: "Analyst / Manager",
  developer_integrator: "Developer / Integrator",
};

/* ============================================================
   SUBSCRIPTION / PLAN LIMITS (USE-01)
   The bot limit comes from the API (GET /api/bots -> meta.plan / meta.botLimit)
   and is enforced there (POST /api/bots -> 403 PLAN_LIMIT_REACHED). The header
   shows "N of LIMIT bots · PLAN plan"; at the limit the Create action stays
   visible but disabled, with the reason and an upgrade hint.
   ============================================================ */

/* ============================================================
   CREATE / EDIT MODAL
   ============================================================ */

function BotFormModal({ open, initial, onClose, onSubmit, submitting, serverError, existingNames }) {
  const isEdit = Boolean(initial);
  const [values, setValues] = useState({ name: "", description: "", model: "", greeting: "" });
  const [catalog, setCatalog] = useState({ models: [], defaultModel: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setValues(
        initial
          ? { name: initial.name, description: initial.description, model: initial.model, greeting: initial.greeting || "" }
          : { name: "", description: "", model: "", greeting: "" }
      );
      setErrors({});
      setTouched({});
      const t = window.setTimeout(() => nameInputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    getAiModels().then(setCatalog).catch(() => setCatalog({ models: [], defaultModel: "" }));
  }, [open]);

  // New bot: preselect the plan's recommended model as soon as the catalogue arrives.
  useEffect(() => {
    if (open && !initial && catalog.defaultModel) setValues((v) => (v.model ? v : { ...v, model: catalog.defaultModel }));
  }, [open, initial, catalog]);

  if (!open) return null;

  const validate = (v) => {
    const e = {};
    const trimmedName = v.name.trim();
    if (!trimmedName) e.name = "Bot name is required.";
    else if (trimmedName.length < 3) e.name = "Bot name must be at least 3 characters.";
    else if (trimmedName.length > 50) e.name = "Bot name must be at most 50 characters.";
    else if (existingNames.includes(trimmedName.toLowerCase())) {
      e.name = "A bot with this name already exists. Choose a different name.";
    }
    if (!v.description.trim()) e.description = "A short description helps your team tell bots apart.";
    else if (v.description.trim().length > 200) e.description = "Description must be at most 200 characters.";
    if (!v.greeting.trim()) e.greeting = "Add a greeting visitors will see first.";
    else if (v.greeting.trim().length > 300) e.greeting = "Greeting must be at most 300 characters.";
    return e;
  };

  const handleChange = (field) => (e) => {
    const next = { ...values, [field]: e.target.value };
    setValues(next);
    if (touched[field]) setErrors(validate(next));
  };

  const handleBlur = (field) => () => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors(validate(values));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const eObj = validate(values);
    setErrors(eObj);
    setTouched({ name: true, description: true, greeting: true });
    if (Object.keys(eObj).length > 0) return;
    onSubmit(values);
  };

  return (
    <Modal open={open} onClose={onClose} closeDisabled={submitting} labelledBy="bot-form-title" scrollable>
      <div className="modal-header">
        <h2 id="bot-form-title" className="modal-title h5 fw-bold mb-0">
          {isEdit ? "Edit bot" : "Create new bot"}
        </h2>
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="modal-body d-flex flex-column gap-3">
          {serverError && (
            <div className="alert alert-danger d-flex align-items-start gap-2 mb-0" role="alert">
              <AlertTriangle size={16} className="flex-shrink-0 mt-1" />
              <span>{serverError}</span>
            </div>
          )}

          <div>
            <FieldLabel required htmlFor="bot-name">
              Bot name
            </FieldLabel>
            <input
              id="bot-name"
              ref={nameInputRef}
              value={values.name}
              onChange={handleChange("name")}
              onBlur={handleBlur("name")}
              aria-invalid={Boolean(errors.name)}
              aria-describedby="name-error"
              placeholder="e.g. Website Support Bot"
              className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
            />
            {errors.name && touched.name && (
              <div id="name-error" className="invalid-feedback">
                {errors.name}
              </div>
            )}
          </div>

          <div>
            <FieldLabel required htmlFor="bot-description">
              Description
            </FieldLabel>
            <input
              id="bot-description"
              value={values.description}
              onChange={handleChange("description")}
              onBlur={handleBlur("description")}
              aria-invalid={Boolean(errors.description)}
              aria-describedby="description-error"
              placeholder="What does this bot handle?"
              className={`form-control ${errors.description && touched.description ? "is-invalid" : ""}`}
            />
            {errors.description && touched.description && (
              <div id="description-error" className="invalid-feedback">
                {errors.description}
              </div>
            )}
          </div>

          <div>
            <FieldLabel htmlFor="bot-model">Model</FieldLabel>
            <select id="bot-model" value={values.model} onChange={handleChange("model")} className="form-select">
              {values.model && !catalog.models.some((m) => m.id === values.model) && <option value={values.model}>{values.model}</option>}
              {catalog.models.map((m) => (
                <option key={m.id} value={m.id} disabled={!m.available}>
                  {m.label}{m.available ? "" : ` — ${m.minPlan} plan`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <FieldLabel required htmlFor="bot-greeting">
              Greeting message
            </FieldLabel>
            <textarea
              id="bot-greeting"
              value={values.greeting}
              onChange={handleChange("greeting")}
              onBlur={handleBlur("greeting")}
              rows={3}
              placeholder="Hi! How can I help you today?"
              aria-invalid={Boolean(errors.greeting)}
              aria-describedby="greeting-error"
              className={`form-control ${errors.greeting && touched.greeting ? "is-invalid" : ""}`}
            />
            {errors.greeting && touched.greeting && (
              <div id="greeting-error" className="invalid-feedback">
                {errors.greeting}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} disabled={submitting} className="btn btn-outline-secondary">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="btn btn-primary d-inline-flex align-items-center gap-2">
            {submitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
            {isEdit ? "Save changes" : "Create bot"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================
   MAIN MODULE
   ============================================================ */

export default function BotManagement({ role: roleProp }) {
  const [loadState, setLoadState] = useState("loading");
  const [bots, setBots] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [formOpen, setFormOpen] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [confirmState, setConfirmState] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3500);
  };
  useEffect(() => () => window.clearTimeout(toastTimerRef.current), []);

  const role = roleProp || "analyst_manager";
  const canManage = ROLES_THAT_CAN_MANAGE_BOTS.includes(role);
  const canViewBots = BOT_VIEW_ROLES.includes(role);

  // The API already scopes bots to the signed-in workspace; every role can view them,
  // only platform/business admins can manage (canManage).
  const visibleBots = bots;

  // Plan usage comes from the API (GET /api/bots meta): { plan, botLimit }.
  const [usage, setUsage] = useState({ plan: "", botLimit: 0 });
  const planLimit = usage.botLimit || 0;
  const atLimit = canManage && planLimit > 0 && visibleBots.length >= planLimit;

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const { items, meta } = await botService.list();
      setBots(items);
      setUsage({ plan: meta?.plan || "", botLimit: meta?.botLimit || 0 });
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return visibleBots.filter((b) => {
      const matchesSearch = b.name.toLowerCase().includes(search.trim().toLowerCase());
      const matchesStatus = statusFilter === "all" || b.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [visibleBots, search, statusFilter]);

  const hasFilters = search.trim().length > 0 || statusFilter !== "all";

  const openCreate = () => {
    setEditingBot(null);
    setFormError("");
    setFormOpen(true);
  };
  const openEdit = (bot) => {
    setEditingBot(bot);
    setFormError("");
    setFormOpen(true);
  };

  const handleFormSubmit = async (values) => {
    setFormSubmitting(true);
    setFormError("");
    try {
      if (editingBot) {
        const updated = await botService.update(editingBot.id, values);
        setBots((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
        showToast("success", `"${updated.name}" updated.`);
      } else {
        const created = await botService.create({ ...values, ownerRole: role });
        setBots((prev) => [created, ...prev]);
        showToast("success", `"${created.name}" created as a draft.`);
      }
      setFormOpen(false);
    } catch (err) {
      setFormError(err.status === 422 || err.status === 409 ? err.message : "We couldn't save this bot. Please try again.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const requestToggleStatus = (bot) => setConfirmState({ type: "status", bot });
  const requestDelete = (bot) => setConfirmState({ type: "delete", bot });

  const handleConfirm = async () => {
    if (!confirmState) return;
    const { type, bot } = confirmState;
    setConfirmBusy(true);
    try {
      if (type === "delete") {
        await botService.remove(bot.id);
        setBots((prev) => prev.filter((b) => b.id !== bot.id));
        showToast("success", `"${bot.name}" deleted.`);
      } else {
        const nextStatus = bot.status === "active" ? "inactive" : "active";
        const updated = await botService.setStatus(bot.id, nextStatus);
        setBots((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
        showToast("success", nextStatus === "active" ? `"${bot.name}" published.` : `"${bot.name}" paused.`);
      }
      setConfirmState(null);
    } catch {
      showToast("error", "That action didn't go through. Please try again.");
    } finally {
      setConfirmBusy(false);
    }
  };

  const rowMenuItems = (bot) =>
    canManage
      ? [
          { key: "edit", label: "Edit", icon: <Pencil size={14} />, onClick: () => openEdit(bot) },
          {
            key: "status",
            label: bot.status === "active" ? "Pause" : "Publish",
            icon: bot.status === "active" ? <Pause size={14} /> : <Play size={14} />,
            onClick: () => requestToggleStatus(bot),
          },
          { key: "delete", label: "Delete", icon: <Trash2 size={14} />, danger: true, onClick: () => requestDelete(bot) },
        ]
      : [];

  // Subscription-limit / disabled-action UI (USE-01): once the plan's bot
  // limit is reached, the Create action is disabled rather than hidden —
  // unlike role-based gating, the user *can* still get access to this
  // action (by upgrading), so the UI explains why instead of removing it.
  const CreateBotAction = ({ label }) => {
    if (!canManage) return null;
    if (atLimit) {
      return (
        <div className="d-flex flex-column align-items-start gap-1">
          <button
            type="button"
            className="btn btn-primary d-inline-flex align-items-center gap-2"
            disabled
            aria-disabled="true"
            title={`Plan limit reached (${bots.length}/${planLimit} bots)`}
          >
            <Plus size={16} />
            {label}
          </button>
          <div className="alert alert-warning py-1 px-2 mb-0 small d-flex align-items-center gap-2">
            <AlertTriangle size={14} className="flex-shrink-0" />
            <span>
              Plan limit reached ({bots.length}/{planLimit} bots).{" "}
              <span className="text-decoration-underline">Upgrade plan</span> to add more.
            </span>
          </div>
        </div>
      );
    }
    return (
      <button onClick={openCreate} className="btn btn-primary d-inline-flex align-items-center gap-2">
        <Plus size={16} />
        {label}
      </button>
    );
  };

  return (
    <div className="bm-page py-4">
      <div className="container" style={{ maxWidth: 960 }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
          <div>
            <div className="d-flex align-items-center gap-2">
              <h1 className="h4 fw-bold mb-0">AI Bots</h1>
              {canViewBots && planLimit > 0 && (
                <span className="badge bg-light text-secondary border fw-normal" data-testid="plan-usage">
                  {visibleBots.length} of {planLimit} bots · {usage.plan} plan
                </span>
              )}
            </div>
            <p className="text-secondary small mb-0">
              {canManage
                ? `Create and manage the bots handling your conversations. Signed in as ${ROLE_LABELS[role]}.`
                : `View bots and their status. ${ROLE_LABELS[role]} does not have bot management permissions.`}
            </p>
          </div>
          <CreateBotAction label="Create New Bot" />
        </div>

        <div className="row g-2 mb-3">
          <div className="col-12 col-md">
            <SearchBox
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bots..."
              ariaLabel="Search bots"
            />
          </div>
          <div className="col-12 col-md-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
              className="form-select"
              style={{ minWidth: 160 }}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-body p-2 p-md-3">
            {loadState === "loading" && (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Bot</th>
                      <th className="d-none d-md-table-cell">Model</th>
                      <th>Version</th>
                      <th className="d-none d-lg-table-cell">Owner</th>
                      <th className="d-none d-md-table-cell">Updated</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody className="placeholder-wave">
                    <RowSkeleton columns={4} />
                    <RowSkeleton columns={4} />
                    <RowSkeleton columns={4} />
                  </tbody>
                </table>
              </div>
            )}

            {loadState === "error" && (
              <ErrorState
                icon={AlertTriangle}
                title="Couldn't load your bots"
                description="Something went wrong reaching the server. Check your connection and try again."
                action={
                  <button onClick={load} className="btn btn-outline-secondary d-inline-flex align-items-center gap-2">
                    <RefreshCw size={16} />
                    Retry
                  </button>
                }
              />
            )}

            {loadState === "ready" && filtered.length === 0 && (
              <EmptyState
                icon={Inbox}
                title={hasFilters ? "No bots match your search" : "You haven't created a bot yet"}
                description={
                  hasFilters
                    ? "Try a different name or clear your filters to see every bot."
                    : canManage
                    ? "Create your first bot to start handling visitor conversations automatically."
                    : "No bots have been created in this workspace yet."
                }
                action={
                  hasFilters ? (
                    <button
                      onClick={() => {
                        setSearch("");
                        setStatusFilter("all");
                      }}
                      className="btn btn-link text-decoration-none"
                    >
                      Clear filters
                    </button>
                  ) : (
                    <CreateBotAction label="Create your first bot" />
                  )
                }
              />
            )}

            {loadState === "ready" && filtered.length > 0 && (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Bot</th>
                      <th className="d-none d-md-table-cell">Model</th>
                      <th>Version</th>
                      <th className="d-none d-lg-table-cell">Owner</th>
                      <th className="d-none d-md-table-cell">Updated</th>
                      <th>Status</th>
                      <th className="text-end">{canManage ? "Actions" : ""}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((bot) => (
                      <tr key={bot.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="bm-avatar">
                              <Bot size={18} />
                            </div>
                            <div>
                              <div className="fw-semibold">{bot.name}</div>
                              <div className="text-secondary small">{bot.description}</div>
                            </div>
                          </div>
                        </td>
                        <td className="d-none d-md-table-cell">{bot.model}</td>
                        <td>v{bot.version}</td>
                        <td className="d-none d-lg-table-cell small">{ROLE_LABELS[bot.ownerRole || "business_admin"]}</td>
                        <td className="d-none d-md-table-cell text-secondary small">{bot.updated}</td>
                        <td>
                          <StatusBadge status={bot.status} />
                        </td>
                        <td className="text-end">
                          <ActionMenu label={`Actions for ${bot.name}`} items={rowMenuItems(bot)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>

      <BotFormModal
        open={formOpen}
        initial={editingBot}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        submitting={formSubmitting}
        serverError={formError}
        existingNames={visibleBots.filter((b) => b.id !== editingBot?.id).map((b) => b.name.toLowerCase())}
      />

      <ConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.type === "delete" ? "Delete this bot?" : confirmState?.bot?.status === "active" ? "Pause this bot?" : "Publish this bot?"}
        description={
          confirmState?.type === "delete"
            ? `"${confirmState?.bot?.name}" will be permanently removed and can't be recovered.`
            : confirmState?.bot?.status === "active"
            ? `"${confirmState?.bot?.name}" will stop responding to visitors until you publish it again.`
            : `"${confirmState?.bot?.name}" will start responding to visitors right away.`
        }
        confirmLabel={confirmState?.type === "delete" ? "Delete" : confirmState?.bot?.status === "active" ? "Pause" : "Publish"}
        danger={confirmState?.type === "delete" || confirmState?.bot?.status === "active"}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmState(null)}
        busy={confirmBusy}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}